import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';

import { BaseMatcherEditorComponent } from '../matchers/interfaces/base-matcher-editor.component';
import { MatcherEditorDirective } from '../matchers/interfaces/matcher-editor.directive';
import { MatcherComponentRegistryService } from '../matchers/service/matcher-component-registry.service';

@Component({
  selector: 'cdw-ae-filter-bar-dialog',
  styleUrls: ['./filter-bar-dialog.scss'],
  templateUrl: './filter-bar-dialog.html',
  standalone: false,
})
export class FilterBarDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatcherEditorDirective, { static: true })
  public matcherTemplate!: MatcherEditorDirective;

  public hasValidMatcherData: boolean | null = false;

  protected isValidating = false;

  protected matcherRef!: ComponentRef<BaseMatcherEditorComponent>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<FilterBarDialogComponent>,
    private matcherRegistry: MatcherComponentRegistryService,
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef,
    @Inject(MAT_DIALOG_DATA) public data: SelectedFilterField
  ) {}

  /**
   * Enter applies the filter — except inside a multi-line input, where it
   * must insert a newline instead.
   *
   * The "Paste a List" mode of the multi-value matchers is fed by hardware
   * barcode scanners that append an Enter keystroke after every scan. Applying
   * on Enter closed the dialog after the first scan and discarded the rest of
   * the list (EDUS-2711). There the filter is applied with the Apply button or
   * the explicit Ctrl/Cmd + Enter shortcut below.
   */
  @HostListener('keydown.enter', ['$event'])
  public onEnterKey(event: Event): void {
    if (this.isMultiLineInput(event.target)) {
      return;
    }
    this.applyIfValid();
  }

  /** Applies the filter from anywhere in the dialog, including a textarea. */
  @HostListener('keydown.control.enter', ['$event'])
  @HostListener('keydown.meta.enter', ['$event'])
  public onApplyShortcut(event: Event): void {
    event.preventDefault();
    this.applyIfValid();
  }

  private isMultiLineInput(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && target.tagName === 'TEXTAREA';
  }

  private applyIfValid(): void {
    if (this.hasValidMatcherData && !this.isValidating) {
      this.close();
    }
  }

  public ngAfterViewInit(): void {
    this.matcherRef.instance.hasErrors$.pipe(takeUntil(this.destroy$)).subscribe(
      (hasError: boolean | null) => {
        this.hasValidMatcherData = !hasError;
        this.changeDetector.detectChanges();
      }
    );
    this.dialogRef.afterOpened().pipe(takeUntil(this.destroy$)).subscribe(() => {
      // `textarea` covers matchers opened straight into "Paste a List" mode, so
      // a barcode scanner has somewhere to type without a click first.
      (this.elementRef.nativeElement.querySelector('input[type="text"], input:not([type]), textarea') as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    const templateRef = this.matcherTemplate.viewContainerRef;
    templateRef.clear();
    const componentClass = this.matcherRegistry.getComponent(this.data.selectedMatcher.name);
    if (!componentClass) {
      throw new Error(`No component registered for matcher: ${this.data.selectedMatcher.name}`);
    }
    this.matcherRef = templateRef.createComponent<BaseMatcherEditorComponent>(componentClass);
    this.matcherRef.instance.matcherOptions = this.data;
  }

  protected close(): void {
    if (!this.matcherRef.instance.validate$) {
      this.dialogRef.close(this.matcherRef.instance.valueToMatch);
      return;
    }
    this.isValidating = true;
    this.changeDetector.detectChanges();
    this.matcherRef.instance.validate$().pipe(take(1), takeUntil(this.destroy$)).subscribe({
      complete: () => {
        this.isValidating = false;
        this.dialogRef.close(this.matcherRef.instance.valueToMatch);
      },
      error: () => {
        this.isValidating = false;
        this.changeDetector.detectChanges();
        // Leave dialog open — the matcher's validate$ surfaces the error via snackbar
      },
    });
  }
}
